import { useCallback } from 'react';
import { useRecoilValue } from 'recoil';
import { IClientEmail, useEmailClient, useEditEmailClient, EmailClientsState } from 'views/email-client/stores';

interface UseClientActionsProps {
	onEdit: () => void;
	onDelete: (clientId: string, clientEmail: string) => void;
}

export const useClientActions = ({ onEdit, onDelete }: UseClientActionsProps) => {
	const emailClientsList = useRecoilValue(EmailClientsState);
	const { makeAsDefaultEmailClient } = useEmailClient();
	const { initializeEditMode } = useEditEmailClient();

	const handleMakeAsDefault = useCallback((client: IClientEmail) => {
		return async () => {
			try {
				await makeAsDefaultEmailClient(client._id);
			} catch (error) {
				// Handle error silently or show user notification
			}
		};
	}, [makeAsDefaultEmailClient]);

	const handleEdit = useCallback((client: IClientEmail) => {
		return () => {
			initializeEditMode(client);
			onEdit();
		};
	}, [initializeEditMode, onEdit]);

	const handleRemove = useCallback((client: IClientEmail) => {
		return () => {
			onDelete(client._id, client.config.email as string);
		};
	}, [onDelete]);

	const getDropOptions = useCallback((client: IClientEmail) => {
		return [
			{
				title: 'Make as Default',
				icon: 'ri-vip-crown-line',
				handleOption: handleMakeAsDefault(client),
				disabled: client?.isActive,
			},
			{
				title: 'Edit',
				icon: 'ri-edit-line',
				handleOption: handleEdit(client),
			},
			{
				title: 'Remove',
				icon: 'ri-delete-bin-line',
				handleOption: handleRemove(client),
				disabled: emailClientsList.length !== 1 && client?.isActive,
				className: 'remove-option',
			},
		];
	}, [handleMakeAsDefault, handleEdit, handleRemove, emailClientsList]);

	return {
		getDropOptions,
	};
}; 